import React from 'react';
import {FormProvider, useFieldArray, useForm, useFormContext} from 'react-hook-form';
import type {ProyectoFormDTO} from '../../../../api/Objetos';
import {informesApi} from '../../../../api/api';
import {createEmptyProyecto} from '../emptyProyecto';
import {DateField, FieldGrid, NumberField, TextField, ToggleField} from '../../../../ui/form/fields';
import {
    EmptyState,
    FormSection,
    ItemCard,
    buildCardTitle,
    buildSummary,
    hasPersistedId,
    useProjectReadOnly,
    BulkToggle
} from '../projectForm.helpers';
import {SectionAlerts, SectionSaveButton, useCollectionSection} from '../sectionCrudUtils';
import {useRegisterSectionSave} from '../SectionSaveCoordinator';
import BarraCargando from "../../../../ui/BarraCargando";
import { useTranslation } from 'react-i18next';

export function InformesSection(props: { proyectoId: number; showIds?: boolean }) {
    const readOnly = useProjectReadOnly();
    const form = useForm<ProyectoFormDTO>({defaultValues: createEmptyProyecto()});
    const section = useCollectionSection({
        queryKey: ['admin', 'proyectos', 'informes'],
        proyectoId: props.proyectoId,
        getFn: informesApi.list,
        createFn: informesApi.create,
        updateFn: informesApi.update,
        deleteFn: informesApi.delete,
    });

    React.useEffect(() => {
        form.reset({...createEmptyProyecto(), informesGestion: section.data ?? []});
    }, [form, section.data]);

    const handleSave = form.handleSubmit(async (value) => {
        await section.save(value.informesGestion ?? []);
    });

    useRegisterSectionSave({
        id: 'informes',
        isDirty: form.formState.isDirty,
        isSaving: section.saveMutation.isPending,
        save: async () => {
            await handleSave();
        },
    });

    return (
        <FormProvider {...form}>
            <InformesSectionFields
                showIds={Boolean(props.showIds)}
                isLoading={section.isLoading}
                loadError={section.error}
            />
            {!readOnly && (
              <>
                <SectionSaveButton onClick={() => void handleSave()} busy={section.saveMutation.isPending}/>
              </>
            )}
        </FormProvider>
    );
}

function InformesSectionFields(props: { showIds: boolean, isLoading: boolean, loadError: any }) {
    const { t } = useTranslation();
    const form = useFormContext<ProyectoFormDTO>();
    const informes = useFieldArray({control: form.control, name: 'informesGestion'});
    const showId = (path: string) => props.showIds && hasPersistedId(form.watch(path as never));
    const readOnly = useProjectReadOnly();

    const setAllPublic = (isPublic: boolean) => {
        informes.fields.forEach((field, index) => {
            form.setValue(`informesGestion.${index}.publico`, isPublic, { shouldDirty: true });
        });
    };

    const currentInformes = form.watch('informesGestion') || [];

    return (
        <FormSection
            id="informes"
            title={t('informesSection.title', { count: informes.fields.length })}
            collapsible
            defaultOpen={false}
            headerActions={!readOnly &&
                <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                    {props.isLoading && <BarraCargando size="1.2em" />}
                    <BulkToggle
                        label={t('informesSection.public')}
                        fields={currentInformes}
                        onToggle={setAllPublic}
                    />
                </div>
            }
        >
            <SectionAlerts loadError={props.loadError}/>
            {informes.fields.length === 0 && <EmptyState message={t('informesSection.noReports')}/>}
            {informes.fields.map((field, index) => (
                <ItemCard
                    key={field.id}
                    title={buildCardTitle(t, t('informesSection.report'), index, form.watch(`informesGestion.${index}.descripcion`), form.watch(`informesGestion.${index}.fecha`))}
                    summary={buildSummary(t, form.watch(`informesGestion.${index}.fecha`), form.watch(`informesGestion.${index}.descripcion`))}
                    onRemove={() => informes.remove(index)}
                    defaultOpen={!hasPersistedId(form.watch(`informesGestion.${index}.id` as never))}
                    actions={!readOnly &&
                        <ToggleField
                            label={t('informesSection.public')}
                            name={`informesGestion.${index}.publico`}
                            register={form.register}
                        />
                    }
                >
                    <FieldGrid>
                        {showId(`informesGestion.${index}.id`) ?
                            <NumberField label={t('informesSection.id')} name={`informesGestion.${index}.id`}
                                         register={form.register}/> : null}
                        <DateField label={t('informesSection.date')} name={`informesGestion.${index}.fecha`} register={form.register}/>
                        <TextField label={t('informesSection.description')} name={`informesGestion.${index}.descripcion`}
                                   register={form.register}/>
                        <TextField label={t('informesSection.reportHyperlink')} name={`informesGestion.${index}.hipervinculoAInforme`}
                                   register={form.register}/>
                    </FieldGrid>
                </ItemCard>
            ))}

            {!readOnly ? (
                <button className="btn btnSecondary" type="button"
                        onClick={() => informes.append({id: 0, fecha: '', descripcion: '', hipervinculoAInforme: '', publico: false})}>
                    {t('informesSection.addReport')}
                </button>
            ) : null}
        </FormSection>
    );
}
